<script setup lang="ts">
import { useFieldTexts, useValidationRules } from '@/composables';
import {
    useAttrs, ref, onMounted,
} from 'vue';
import type { VSwitch } from 'vuetify/components';

type PropsType = {
    modelValue?: boolean;
    label?: string;
    color?: string;
    baseColor?: string;
    density?: VSwitch['$props']['density'];
    direction?: VSwitch['$props']['direction'];
    inset?: boolean;
    flat?: boolean;
    hideDetails?: boolean | 'auto';
    isLabelTranslated?: boolean;
    showRequiredVisual?: boolean;
    name?: string;
    dataType?: string;
};

const props = withDefaults(defineProps<PropsType>(), {
    modelValue: false,
    color: 'primary',
    baseColor: 'grey-700',
    density: 'compact',
    direction: 'horizontal',
    inset: true,
    flat: true,
    hideDetails: true,
    isLabelTranslated: true,
    showRequiredVisual: true,
    dataType: 'boolean',
});

const emit = defineEmits<{
    'update:modelValue': [value: boolean | null];
    'update:indeterminate': [value: boolean];
    'update:focused': [value: boolean];
}>();

defineOptions({ inheritAttrs: false });

const attrs = useAttrs();

const { getLabel } = useFieldTexts({
    label: props.label,
    isTranslated: props.isLabelTranslated,
});

const { rules, isRequired } = useValidationRules(attrs);

const switchRef = ref<VSwitch | null>(null);

onMounted(() => {
    const input = switchRef.value?.$el?.querySelector('input[type="checkbox"]');
    if (input) {
        input.dataset.type = props.dataType;
    }
});
</script>

<template>
    <VSwitch
        ref="switchRef"
        :model-value="modelValue"
        :label="getLabel"
        :color="color"
        :base-color="baseColor"
        :density="density"
        :direction="direction"
        :inset="inset"
        :flat="flat"
        :hide-details="hideDetails"
        :name="name"
        :class="[$attrs.class, { required: isRequired && showRequiredVisual }]"
        v-bind="{
            ...$attrs,
            rules: rules,
        }"
        @update:indeterminate="emit('update:indeterminate', $event)"
        @update:focused="emit('update:focused', $event)"
        @update:model-value="emit('update:modelValue', $event)"
    >
        <template
            v-if="$slots.prepend"
            #prepend
        >
            <slot name="prepend" />
        </template>

        <template
            v-if="$slots.append"
            #append
        >
            <slot name="append" />
        </template>

        <template
            v-if="$slots.label"
            #label
        >
            <slot name="label" />
        </template>

        <template
            v-if="$slots.message"
            #message="{ message }"
        >
            <slot
                name="message"
                :message="message"
            />
        </template>

        <template
            v-if="$slots.details"
            #details
        >
            <slot name="details" />
        </template>

        <template
            v-if="$slots.loader"
            #loader
        >
            <slot name="loader" />
        </template>
    </VSwitch>
</template>
